"use client";

import { formatDistanceToNow } from "date-fns";

import { type Bid, type Prisma } from "@/.zenstack/logical-prisma-client";
import ApartmentDibsAvatar from "@/components/ui/ApartmentDibsAvatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useRequireAuth } from "@/hooks/useAuthRedirect";
import {
  type AuctionDetails,
  getAuctionBiddingDetails,
} from "@/lib/bids/bidding";

// export type BidHistoryBid = Pick<
//   Bid,
//   "id" | "currentBidAmount" | "propertySeekerId" | "createdAt" | "placedBy"
// >;

export type BidsHistoryBids = Prisma.BidGetPayload<{
  select: {
    id: true;
    currentBidAmount: true;
    createdById: true;
    maxBidAmount: true;
    placedBy: true;
    createdAt: true;
    propertySeekerId: true;
  };
}>;

interface BidHistoryProps {
  auction: AuctionDetails;
  bids?: BidsHistoryBids[];
  maxHeight?: string;
  className?: string;
}

export function BidHistory({ auction, bids = [] }: BidHistoryProps) {
  const { session } = useRequireAuth();

  const biddingDetails = getAuctionBiddingDetails(auction, bids);
  console.table(biddingDetails.bidsGroupedByUserId(session?.user?.id));

  return (
    <div className="space-y-4">
      <ScrollArea className="max-h-[60vh] overflow-auto">
        <div className="space-y-2 pr-2">
          {biddingDetails.bidsGroupedByUserId(session?.user?.id).map((bid) => (
            <div
              key={bid.id}
              className="grid grid-cols-[200px_auto_auto_auto] items-start gap-2 border-b border-border py-3 last:border-0"
            >
              {/* Bidder Column */}
              <div className="flex max-w-[200px] min-w-0 items-center gap-2">
                {session?.user?.id &&
                (bid as Pick<Bid, "propertySeekerId">).propertySeekerId ===
                  session.user.id ? (
                  <>
                    <ApartmentDibsAvatar className="h-6 w-6" />
                    <span className="text-sm font-medium whitespace-nowrap">
                      {session.user.name}
                    </span>
                  </>
                ) : (
                  <span className="text-sm whitespace-nowrap text-muted-foreground">
                    Bidder {biddingDetails.bidPosition(bid.propertySeekerId)}
                  </span>
                )}
              </div>

              {/* Bid Amounts Column */}
              <div className="flex flex-col items-end gap-1">
                {/* Current Bid Amount */}
                <div className="text-right">
                  <span className="text-lg font-semibold">
                    ${Number(bid.currentBidAmount).toLocaleString()}
                  </span>
                </div>

                {/* Max Bid Amount (if different from current) */}
                {bid.propertySeekerId === session?.user?.id &&
                  (bid.updatedMaxBidAmount || bid.addedMaxBidAmount) && (
                    <div className="text-right">
                      <span className="text-xs text-muted-foreground">
                        Max: ${Number(bid.maxBidAmount).toLocaleString()}
                      </span>
                    </div>
                  )}
              </div>

              {/* Status Badges Column */}
              <div className="flex flex-col gap-1">
                {biddingDetails.highestPlacedBid?.id === bid.id && (
                  <Badge variant="secondary" className="text-xs">
                    Winning
                  </Badge>
                )}
                {bid.propertySeekerId === session?.user?.id && (
                  <>
                    {bid.updatedMaxBidAmount && (
                      <Badge variant="outline" className="text-xs">
                        Max Bid Updated
                      </Badge>
                    )}
                    {bid.addedMaxBidAmount && (
                      <Badge variant="outline" className="text-xs">
                        Max Bid Added
                      </Badge>
                    )}
                    {bid.placedBy === "AUTO_BID" && (
                      <Badge variant="outline" className="text-xs">
                        Proxy Bid
                      </Badge>
                    )}
                  </>
                )}
              </div>

              {/* Time Column */}
              <div className="text-right">
                <div className="text-sm whitespace-nowrap text-muted-foreground">
                  {formatDistanceToNow(new Date(bid.createdAt), {
                    addSuffix: true,
                  })}
                </div>
              </div>
            </div>
          ))}
          {biddingDetails.bidCount === 0 && (
            <div className="py-2 text-center text-sm text-muted-foreground">
              No bids yet
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
